import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { UI } from '../../';
import { useAppState } from '../../../Context/AppStateContext';
import Taskbar from '../components/Taskbar.component';
import About from '../modals/About.Component';
import Career from '../modals/Career.Component';
import Contact from '../modals/Contact.Component';
import Header from '../modals/Header.Component';
import Projects from '../modals/Projects.Component';
import JobAnalysis from '../modals/JobAnalysis.Component';

interface DashboardProps {
  defaultModal?: string;
}

const Dashboard: React.FC<DashboardProps> = ({ defaultModal }) => {
  console.log('[Dashboard] Render, defaultModal:', defaultModal);
  const { minimizedWindows } = useAppState();
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language;

  const [activeTabs, setActiveTabs] = useState<{ [key: string]: boolean }>({
    About: false,
    Career: false,
    Projects: false,
    Contact: false,
    JobAnalysis: false,
  });

  // Track which modal is currently active (focused/on top)
  const [activeModal, setActiveModal] = useState<string | null>(null);

  // Track if we're doing programmatic navigation (to prevent defaultModal effect from reopening)
  const isNavigatingRef = useRef(false);
  // Track if initial mount has completed (to prevent race condition with defaultModal)
  const hasInitializedRef = useRef(false);

  // Auto-open specified modal on mount (skip if we navigated programmatically)
  useEffect(() => {
    console.log('[defaultModal effect] defaultModal:', defaultModal, 'isNavigating:', isNavigatingRef.current, 'hasInitialized:', hasInitializedRef.current);
    if (isNavigatingRef.current) {
      console.log('[defaultModal effect] Skipping - programmatic navigation');
      isNavigatingRef.current = false;
      hasInitializedRef.current = true;
      return;
    }
    if (defaultModal) {
      const modalMap: { [key: string]: string } = {
        about: 'About',
        career: 'Career',
        projects: 'Projects',
        contact: 'Contact',
        jobanalysis: 'JobAnalysis',
      };

      const modalName = modalMap[defaultModal];
      if (modalName) {
        console.log('[defaultModal effect] Opening modal:', modalName);
        setActiveTabs(prev => ({ ...prev, [modalName]: true }));
        setActiveModal(modalName);
      }
    }
    hasInitializedRef.current = true;
  }, [defaultModal]);

  // Update URL when active modal changes
  useEffect(() => {
    console.log('[URL effect] activeModal:', activeModal, 'activeTabs:', activeTabs, 'pathname:', location.pathname);
    const routeMap: { [key: string]: { no: string; en: string } } = {
      'About': { no: '/om-meg', en: '/en/about' },
      'Career': { no: '/karriere', en: '/en/career' },
      'Projects': { no: '/prosjekter', en: '/en/projects' },
      'Contact': { no: '/kontakt', en: '/en/contact' },
      'JobAnalysis': { no: '/jobbanalyse', en: '/en/job-analysis' },
    };

    if (activeModal) {
      const route = currentLanguage === 'en' ? routeMap[activeModal]?.en : routeMap[activeModal]?.no;
      if (route && location.pathname !== route) {
        console.log('[URL effect] Navigating to modal route:', route);
        isNavigatingRef.current = true;
        navigate(route, { replace: true });
      }
    } else {
      // When no modal is active, check if any modals are still open
      const anyModalOpen = Object.values(activeTabs).some(isOpen => isOpen);
      console.log('[URL effect] No activeModal, anyModalOpen:', anyModalOpen, 'defaultModal:', defaultModal);
      if (!anyModalOpen) {
        // Don't navigate to home if we're on a route that matches defaultModal
        // This handles the race condition where state hasn't updated yet
        const modalRouteMap: { [key: string]: string[] } = {
          'about': ['/om-meg', '/en/about'],
          'career': ['/karriere', '/en/career'],
          'projects': ['/prosjekter', '/en/projects'],
          'contact': ['/kontakt', '/en/contact'],
          'jobanalysis': ['/jobbanalyse', '/en/job-analysis'],
        };
        if (defaultModal && modalRouteMap[defaultModal]?.includes(location.pathname)) {
          console.log('[URL effect] Skipping home navigation - waiting for defaultModal to open');
          return;
        }
        // Navigate to home when all modals are closed
        const homeRoute = currentLanguage === 'en' ? '/en' : '/';
        const homeRoutes = ['/', '/en'];
        if (!homeRoutes.includes(location.pathname)) {
          console.log('[URL effect] Navigating to home:', homeRoute);
          isNavigatingRef.current = true;
          navigate(homeRoute, { replace: true });
        }
      }
    }
  }, [activeModal, activeTabs, currentLanguage, location.pathname, navigate]);

  // Handle route changes (back button, direct navigation)
  useEffect(() => {
    console.log('[Route effect] pathname changed to:', location.pathname);
    const homeRoutes = ['/', '/en'];

    if (homeRoutes.includes(location.pathname)) {
      console.log('[Route effect] At home route, closing all modals');
      // Close all modals when navigating to home
      setActiveTabs({
        About: false,
        Career: false,
        Projects: false,
        Contact: false,
        JobAnalysis: false,
      });
      setActiveModal(null);
    }
  }, [location.pathname]);

  const getModalHeader = (modalName: string) => {
    switch (modalName) {
      case 'About':
        return 'ABOUT ME';
      case 'Career':
        return 'CAREER';
      case 'Projects':
        return 'PROJECT PORTFOLIO';
      case 'Contact':
        return 'CONTACT';
      case 'JobAnalysis':
        return 'JOB FIT ANALYZER';
      default:
        return modalName.toUpperCase();
    }
  };

  // Helper function to handle modal toggling
  const toggleModal = (modalName: string) => {
    console.log('[toggleModal] Called with:', modalName, 'current activeTabs:', activeTabs);
    // Check if the modal is minimized
    const modalHeader = getModalHeader(modalName);
    const minimizedWindow = minimizedWindows.find(window => window.header === modalHeader);

    if (minimizedWindow) {
      // If it's minimized, restore it and make it active
      console.log('[toggleModal] Restoring minimized window:', modalName);
      minimizedWindow.restore();
      setActiveModal(modalName);
    } else {
      // If it's not minimized, toggle its visibility
      const isCurrentlyOpen = activeTabs[modalName];
      console.log('[toggleModal] isCurrentlyOpen:', isCurrentlyOpen);
      setActiveTabs(prev => ({ ...prev, [modalName]: !prev[modalName] }));

      if (isCurrentlyOpen) {
        // Closing the modal - if it was active, clear active modal
        console.log('[toggleModal] Closing modal:', modalName);
        if (activeModal === modalName) {
          setActiveModal(null);
        }
      } else {
        // Opening the modal - make it active
        console.log('[toggleModal] Opening modal:', modalName);
        setActiveModal(modalName);
      }
    }
  };

  // Handle modal focus (when user clicks on a modal)
  const handleModalFocus = (modalName: string) => {
    console.log('[handleModalFocus] Called with:', modalName, 'isOpen:', activeTabs[modalName]);
    // Only set active if the modal is still open
    if (activeTabs[modalName]) {
      setActiveModal(modalName);
    }
  };

  // Handle modal close
  const handleModalClose = (modalName: string) => {
    console.log('[handleModalClose] Called with:', modalName, 'activeModal:', activeModal);

    setActiveTabs(prev => {
      console.log('[handleModalClose] Setting activeTabs, prev:', prev);
      const newTabs = { ...prev, [modalName]: false };

      // Use functional update for activeModal to avoid stale closure
      setActiveModal(currentActive => {
        console.log('[handleModalClose] currentActive:', currentActive);
        if (currentActive === modalName) {
          // Find another open modal to make active, or null if none
          const nextActive = Object.keys(newTabs).find(key => newTabs[key]) || null;
          console.log('[handleModalClose] Setting nextActive:', nextActive);
          return nextActive;
        }
        return currentActive;
      });

      return newTabs;
    });
  };

  return (
    <div>
      <Header
        onAboutClick={() => toggleModal('About')}
        onCareerClick={() => toggleModal('Career')}
        onProjectsClick={() => toggleModal('Projects')}
        onContactClick={() => toggleModal('Contact')}
        onJobAnalysisClick={() => toggleModal('JobAnalysis')}
        activeTabs={activeTabs}
      />
      <About
        visible={activeTabs['About']}
        onClose={() => handleModalClose('About')}
        onFocus={() => handleModalFocus('About')}
        isActive={activeModal === 'About'}
      />
      <Career
        visible={activeTabs['Career']}
        onClose={() => handleModalClose('Career')}
        onFocus={() => handleModalFocus('Career')}
        isActive={activeModal === 'Career'}
      />
      <Projects
        visible={activeTabs['Projects']}
        onClose={() => handleModalClose('Projects')}
        onFocus={() => handleModalFocus('Projects')}
        isActive={activeModal === 'Projects'}
      />
      <Contact
        visible={activeTabs['Contact']}
        onClose={() => handleModalClose('Contact')}
        onFocus={() => handleModalFocus('Contact')}
        isActive={activeModal === 'Contact'}
      />
      <JobAnalysis
        visible={activeTabs['JobAnalysis']}
        onClose={() => handleModalClose('JobAnalysis')}
        onFocus={() => handleModalFocus('JobAnalysis')}
        isActive={activeModal === 'JobAnalysis'}
      />
      <UI.Links />
      <Taskbar />
    </div>
  );
};

export default Dashboard;
